import { useId, useRef } from 'react';
import type { JSX, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useOverlayMotion } from '../lib/useOverlayMotion.js';
import { useDialogFocus } from '../lib/useDialogFocus.js';
import { IconClose } from '../icons/index.js';
import { Button } from './Button.js';
import { HorsBlocDeReglages } from './Settings.js';

/**
 * Modal : la pop-up centrale STANDARD des apps MyTaki.
 *
 * Usage : fenetre ponctuelle de confirmation ou de choix. La creation et
 * l'edition des fiches vivent dans la fiche elle-meme, pas dans un modal.
 *
 * Doctrine : un seul modal ouvert a la fois par ecran (un seul etat openModal
 * par ecran, ex. `useState<'outing' | 'cat' | null>(null)`).
 *
 * Fermeture : clic sur le voile, touche Escape, ou un bouton de l'app dans
 * `footer` qui appelle `onClose`.
 *
 * Motion : ouverture ET fermeture animees d'office (voile en fondu, panneau
 * en pop, utilitaires taki-anim-* du socle). C'est la norme de tout overlay
 * MyTaki : une app n'ecrit JAMAIS d'animation, elle bascule juste `open`.
 */
export interface ModalProps {
  /** Ouvert (rendu) ou non. Rien ne se rend si faux. */
  open: boolean;
  /** Fermeture : clic sur le voile, touche Escape, ou bouton de l'app. */
  onClose: () => void;
  /** Titre du panneau, rendu en tete (h2) et utilise comme nom accessible. */
  title: string;
  /** Precision optionnelle rendue sous le titre. */
  description?: string;
  /**
   * Largeur du panneau : sm w-[25rem], md w-[32rem] (defaut), lg w-[40rem].
   *
   * `plein` EST LA FENETRE D'UNE VISIONNEUSE (une image a montrer le plus grand
   * possible) : PAS DE PANNEAU (recette du 02/11 : le cadre blanc mangeait la
   * place). Le contenu se pose a meme le voile floute, sur tout l'ecran ; le
   * titre n'est lu que par les lecteurs d'ecran, le sous-titre (« 2 / 5 ») se
   * lit en petit en haut a gauche, le pied devient la barre d'outils en haut a
   * droite, a gauche de la croix. Le coin bas droit, ou se pose la mascotte de
   * Lia, ne porte donc aucun geste. La surface laisse passer les clics
   * (`pointer-events-none`) : un clic a cote de l'image tombe sur le voile et
   * ferme ; le contenu rend cliquable ce qui doit l'etre (`pointer-events-auto`).
   *
   * `xl` (et `plein`) POSENT AUSSI UNE HAUTEUR, et c'est ce qui les distingue :
   * les trois autres tiennent la largeur et laissent le contenu decider du
   * reste. Une fenetre de TRAVAIL (chercher, filtrer, parcourir un tableau) ne
   * peut pas faire ca : sa hauteur changerait a chaque frappe de recherche, et
   * le pied remonterait sous le doigt qui vise une ligne. Elle garde donc une
   * surface stable, plafonnee sur les grands ecrans, et c'est son corps qui
   * defile.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'plein';
  /** Corps (formulaire). Defile si trop haut. */
  children: ReactNode;
  /** Rangee d'actions optionnelle en pied de panneau (boutons @taki/ui). */
  footer?: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'w-[25rem]',
  md: 'w-[32rem]',
  lg: 'w-[40rem]',
  xl: 'h-[min(42rem,80dvh)] w-[min(72rem,calc(100vw-2rem))]',
  plein: 'h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)]',
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const isWorkspace = size === 'xl';
  const nu = size === 'plein';

  // Cycle de vie anime (norme du socle) : quand `open` passe a faux, le
  // modal reste monte le temps de l'animation de sortie (phase `closing`,
  // classes taki-anim-*-out) puis disparait.
  const { mounted, closing } = useOverlayMotion(open);

  useDialogFocus(open && mounted && !closing, panelRef, onClose, 30);

  if (!mounted) return null;

  return (
    // z-30 : SOUS le lanceur rapide du shell (z-40), pour qu'il puisse
    // s'ouvrir par-dessus un modal d'app.
    <div
      className={cn(
        'fixed inset-0 z-30 flex items-center justify-center p-4',
        // UNE FENETRE MOYENNE NE PASSE JAMAIS SOUS LE LANCEUR DE LIA : il vit
        // au-dessus de tout (z-50, coin bas droit) et publie sa hauteur
        // (`--taki-launcher-clearance`, `LiaLauncher.tsx`), la meme regle que
        // la derniere ligne d'une liste. Au-dela de 1280 px, une fenetre de
        // 40rem au plus, centree dans la zone, n'atteint pas la colonne du
        // lanceur : rien ne change. En dessous, une marge SYMETRIQUE la garde
        // centree et hors de sa bande ; sans lanceur, la variable manque. Les
        // fenetres de travail (`xl`, `plein`) gardent leur hauteur.
        !isWorkspace && size !== 'plein' && 'max-xl:py-[max(1rem,var(--taki-launcher-clearance,1rem))]',
        // Pendant la sortie, l'overlay laisse passer les clics : l'ecran
        // redessous est utilisable sans attendre la fin du fondu.
        closing && 'pointer-events-none',
      )}
    >
      {/* Voile : token translucide dedie (bg-scrim), jamais `bg-bg/xx`. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-scrim backdrop-blur-sm',
          closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
        )}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description != null ? descriptionId : undefined}
        className={cn(
          'relative z-10 flex max-w-full flex-col outline-none',
          SIZE_CLASSES[size],
          nu ? 'pointer-events-none' : 'rounded-2xl border border-border bg-surface shadow-panel',
          isWorkspace ? 'max-h-[calc(100dvh-2rem)]' : !nu && 'max-h-[80%] p-4',
          closing ? 'taki-anim-pop-out' : 'taki-anim-pop',
        )}
      >
        {nu ? (
          <header className="flex h-9 shrink-0 items-center pr-24">
            <h2 id={titleId} className="sr-only">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="text-sm tabular-nums text-ink-700">
                {description}
              </p>
            )}
          </header>
        ) : isWorkspace ? (
          <header className="flex shrink-0 flex-col gap-1 px-6 pb-5 pt-5 pr-16">
            <h2 id={titleId} className="text-lg font-semibold text-ink-900">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="text-xs text-ink-500">
                {description}
              </p>
            )}
          </header>
        ) : (
          <div className="shrink-0 pb-3">
            <h2 id={titleId} className="text-sm font-semibold text-ink-900">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="mt-1 text-xs text-ink-500">
                {description}
              </p>
            )}
          </div>
        )}

        {/* Corps : seule partie qui defile si le contenu est trop haut. */}
        {/* Ouverte depuis une section de reglages, la fenetre garde ses messages chez elle. */}
        <div className={cn('min-h-0 flex-1', nu ? 'overflow-hidden' : 'overflow-y-auto', isWorkspace && 'px-6 pb-5')}>
          <HorsBlocDeReglages>{children}</HorsBlocDeReglages>
        </div>

        {nu && (
          /* La barre d'outils d'une visionneuse : le pied, puis la croix, en haut
             a droite. Sans cadre autour, un `ghost` ne se lirait pas comme un
             bouton : la croix est `secondary`, comme les gestes du pied (G28). */
          <div className="pointer-events-auto absolute right-0 top-0 flex items-center gap-2">
            <HorsBlocDeReglages>{footer}</HorsBlocDeReglages>
            <Button variant="secondary" size="sm" onClick={onClose} aria-label="Fermer" title="Fermer" className="h-9 w-9 px-0">
              <IconClose size={16} />
            </Button>
          </div>
        )}

        {footer != null && !nu && (
          <div
            className={cn(
              'flex shrink-0 items-center justify-end gap-2',
              isWorkspace ? 'border-t border-border-soft px-6 py-4' : 'pt-3',
            )}
          >
            <HorsBlocDeReglages>{footer}</HorsBlocDeReglages>
          </div>
        )}

        {isWorkspace && (
          /* Rendu apres le corps : le premier focus reste celui de la recherche. */
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fermer"
            title="Fermer"
            className="absolute right-4 top-4 w-8 px-0"
          >
            <IconClose size={14} />
          </Button>
        )}
      </div>
    </div>
  );
}

export default Modal;
